import { HTTPError } from 'ky';
import { z, ZodError } from 'zod';
import { apiFor } from './client';

/*
 * Admin pipeline-run statistics API (the "report generation log").
 *
 * Unlike admin/users (central registry only), pipeline runs live in EACH
 * cluster's reporting DB — the Brazil backend only knows Brazil's runs, the EU
 * backend knows Spain/France/AH. Every fetcher therefore takes the cluster
 * baseUrl; the page lets the admin pick which backend to inspect. The unified
 * admin JWT is accepted by all of them (apiFor attaches it).
 */

export const pipelineRunSchema = z.object({
  id: z.string(),
  pipeline_name: z.string(),
  client_id: z.string().nullable(),
  report_id: z.string().nullable(),
  triggered_by: z.string(),
  // Which process/actor spawned the run: the scheduler's per-API-process
  // instance id ("host#pid#rand") or an admin id. Optional — older backends
  // don't send it. Several distinct values for one pipeline = several API
  // replicas each running a scheduler.
  spawned_by: z.string().nullable().optional(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  duration_ms: z.number().nullable(),
  // The list endpoint returns exit_code; the steps endpoint's run header omits
  // it. Optional so one schema serves both (the UI doesn't render it).
  exit_code: z.number().nullable().optional(),
  status: z.enum(['running', 'completed', 'failed', 'timeout', 'stopped']),
  error: z.string().nullable(),
});

export type PipelineRun = z.infer<typeof pipelineRunSchema>;

export const pipelineRunStepSchema = z.object({
  seq: z.number(),
  step: z.string(),
  duration_ms: z.number(),
  status: z.enum(['ok', 'error']),
  error: z.string().nullable(),
  started_at: z.string(),
});

export type PipelineRunStep = z.infer<typeof pipelineRunStepSchema>;

const runsResponseSchema = z.object({ data: z.array(pipelineRunSchema) });
const stepsResponseSchema = z.object({
  data: z.object({ run: pipelineRunSchema, steps: z.array(pipelineRunStepSchema) }),
});

export class AdminPipelinesApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'AdminPipelinesApiError';
  }
}

async function toApiError(err: unknown): Promise<AdminPipelinesApiError> {
  if (err instanceof HTTPError) {
    let message = err.response.statusText || 'Request failed';
    try {
      const body = (await err.response.json()) as { error?: { message?: string } | string };
      if (typeof body?.error === 'string') message = body.error;
      else if (body?.error?.message) message = body.error.message;
    } catch {
      /* non-JSON body — keep the status text */
    }
    return new AdminPipelinesApiError(message, err.response.status);
  }
  // A schema mismatch is a bug/contract drift, not connectivity — say so
  // instead of the misleading "could not reach the server".
  if (err instanceof ZodError) {
    return new AdminPipelinesApiError('Unexpected response shape from the server (schema mismatch)', 0);
  }
  return new AdminPipelinesApiError('Could not reach the server', 0);
}

/** Last `limit` report generations on one cluster backend, newest first. */
export async function listPipelineRuns(baseUrl: string, limit = 10): Promise<PipelineRun[]> {
  try {
    const raw = await apiFor(baseUrl).get('admin/pipelines/runs', { searchParams: { limit } }).json();
    return runsResponseSchema.parse(raw).data;
  } catch (err) {
    throw await toApiError(err);
  }
}

/** Per-step timing breakdown of one run (steps in execution order). */
export async function getPipelineRunSteps(baseUrl: string, runId: string): Promise<{ run: PipelineRun; steps: PipelineRunStep[] }> {
  try {
    const raw = await apiFor(baseUrl).get(`admin/pipelines/runs/${runId}/steps`).json();
    return stepsResponseSchema.parse(raw).data;
  } catch (err) {
    throw await toApiError(err);
  }
}

const triggerResponseSchema = z.object({ data: z.object({ run_id: z.string().optional() }) });

/** Start a report's pipeline on its backend. Returns the new run id (202). */
export async function triggerPipeline(baseUrl: string, reportId: string): Promise<string | null> {
  try {
    const raw = await apiFor(baseUrl).post(`admin/reports/${reportId}/refresh`).json();
    return triggerResponseSchema.parse(raw).data.run_id ?? null;
  } catch (err) {
    throw await toApiError(err);
  }
}

/** Stop a running admin-triggered run. 409s explain why a run can't be stopped. */
export async function stopPipelineRun(baseUrl: string, runId: string): Promise<void> {
  try {
    await apiFor(baseUrl).post(`admin/pipelines/runs/${runId}/stop`).json();
  } catch (err) {
    throw await toApiError(err);
  }
}

export const pipelineScheduleSchema = z.object({
  pipeline_name: z.string(),
  report_id: z.string().nullable(),
  interval_sec: z.number().nullable(),
  daily_at_utc: z.string().nullable(),
  enabled: z.boolean(),
  retired: z.boolean().optional(),
  updated_at: z.string().nullable(),
  updated_by: z.string().nullable(),
});

export type PipelineSchedule = z.infer<typeof pipelineScheduleSchema>;

const scheduleResponseSchema = z.object({
  data: z.array(pipelineScheduleSchema),
  meta: z.object({ scheduler_enabled: z.boolean().optional() }).passthrough().optional(),
});

/** Automatic-run flags for every schedulable pipeline on one backend. */
export async function listPipelineSchedule(baseUrl: string): Promise<{ pipelines: PipelineSchedule[]; schedulerEnabled: boolean }> {
  try {
    const raw = await apiFor(baseUrl).get('admin/pipelines/schedule').json();
    const parsed = scheduleResponseSchema.parse(raw);
    return { pipelines: parsed.data, schedulerEnabled: parsed.meta?.scheduler_enabled ?? true };
  } catch (err) {
    throw await toApiError(err);
  }
}

/** Enable/disable a pipeline's automatic runs. Takes effect on the next tick. */
export async function setPipelineScheduleEnabled(baseUrl: string, pipelineName: string, enabled: boolean): Promise<void> {
  try {
    await apiFor(baseUrl).put(`admin/pipelines/schedule/${pipelineName}`, { json: { enabled } }).json();
  } catch (err) {
    throw await toApiError(err);
  }
}

/** Retire (or restore) a pipeline: retired never auto-runs and is hidden from the list. */
export async function setPipelineRetired(baseUrl: string, pipelineName: string, retired: boolean): Promise<void> {
  try {
    await apiFor(baseUrl).put(`admin/pipelines/schedule/${pipelineName}`, { json: { retired } }).json();
  } catch (err) {
    throw await toApiError(err);
  }
}

export const pipelineConfigKeySchema = z.object({
  key: z.string(),
  env_var: z.string(),
  env_value: z.string().nullable(),
  db_value: z.string().nullable(),
});

export const pipelineConfigSchema = z.object({
  pipeline_name: z.string(),
  keys: z.array(pipelineConfigKeySchema),
  updated_at: z.string().nullable(),
  updated_by: z.string().nullable(),
});

export type PipelineConfig = z.infer<typeof pipelineConfigSchema>;

const configResponseSchema = z.object({
  data: z.array(pipelineConfigSchema),
  // Retired pipelines are omitted from `data`; their names come back here so the
  // page can say how many were hidden instead of them vanishing silently.
  meta: z
    .object({ hidden_retired: z.array(z.string()).optional() })
    .passthrough()
    .optional(),
});

/** Admin-managed identifiers for the ACTIVE pipelines (retired ones excluded). */
export async function listPipelineConfig(baseUrl: string): Promise<{ pipelines: PipelineConfig[]; hiddenRetired: string[] }> {
  try {
    const raw = await apiFor(baseUrl).get('admin/pipelines/config').json();
    const parsed = configResponseSchema.parse(raw);
    return { pipelines: parsed.data, hiddenRetired: parsed.meta?.hidden_retired ?? [] };
  } catch (err) {
    throw await toApiError(err);
  }
}

/** Merge config keys for one pipeline (null deletes a key → env fallback). */
export async function updatePipelineConfig(baseUrl: string, pipelineName: string, config: Record<string, string | null>): Promise<void> {
  try {
    await apiFor(baseUrl).put(`admin/pipelines/config/${pipelineName}`, { json: { config } }).json();
  } catch (err) {
    throw await toApiError(err);
  }
}
